import React, { useState, useEffect, useRef } from 'react';
import { Play } from 'lucide-react';
import serviceInfoImg from '../../assets/dentalServiceInfoImg.jpg';
import { Link } from 'react-router';

const ServiceInformationSection = ({ serviceInfo }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={sectionRef}
      className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
        <svg width="100%" height="100%" viewBox="0 0 200 400" xmlns="http://www.w3.org/2000/svg">
          <path d="M150 50C180 80 150 150 180 180C210 210 180 270 150 300C120 330 90 300 60 330C30 360 0 330 0 300V0H300C300 0 270 20 240 30C210 40 120 20 150 50Z" fill="#00B2C2" />
        </svg>
      </div>
      
      <div className="absolute bottom-0 left-0 w-1/4 h-1/2 opacity-5 pointer-events-none">
        <svg width="100%" height="100%" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 150C-10 120 0 70 30 40C60 10 120 0 150 30C180 60 190 120 160 150C130 180 70 190 40 160C10 130 50 180 20 150Z" fill="#A6CE39" />
        </svg>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 flex flex-col lg:flex-row items-center gap-8 lg:gap-12 relative">
        {/* Left Content */}
        <div className={`lg:w-1/2 space-y-6 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
          <div className="inline-block">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-800">
              {serviceInfo?.title || 'Routine care for all'}
            </h2>
            <div className="h-1 w-20 bg-teal-500 mt-2 transform transition-transform duration-700 ease-out scale-x-0 origin-left" style={{
              transform: isVisible ? 'scaleX(1)' : 'scaleX(0)',
              transitionDelay: '300ms'
            }}></div>
          </div>
          
          <p className="text-gray-700 text-base md:text-lg">
            {serviceInfo?.description || 'We make getting your teeth cleaned convenient and, dare we say, kind of fun. Visit us every 6 months for a 60-minute visit that includes:'}
          </p>
          
          <ul className="space-y-4">
            {serviceInfo?.points?.map((point, index) => (
              <li key={index} className="flex items-start transition-all duration-500 transform" style={{
                transitionDelay: `${300 + (index * 150)}ms`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)'
              }}>
                <span className="flex-shrink-0 w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center mr-3 mt-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12L10 17L19 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className="text-gray-700">{point}</span>
              </li>
            )) || (
              <>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center mr-3 mt-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12L10 17L19 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="text-gray-700">Comprehensive examination of teeth and gums</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center mr-3 mt-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12L10 17L19 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="text-gray-700">Professional cleaning and polishing</span>
                </li>
                <li className="flex items-start">
                  <span className="flex-shrink-0 w-6 h-6 bg-lime-400 rounded-full flex items-center justify-center mr-3 mt-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12L10 17L19 8" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="text-gray-700">Digital X-rays when necessary</span>
                </li>
              </>
            )}
          </ul>
          
          <div className="pt-2 transition-all duration-700" style={{
            transitionDelay: '800ms',
            opacity: isVisible ? 1 : 0
          }}>
            <span className="text-gray-600 ml-4">{serviceInfo?.tagline}</span>
          </div>
        </div>

        {/* Right Image */}
        <div className={`lg:w-1/2 relative transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '200ms' }}>
          <div className="relative rounded-xl overflow-hidden shadow-xl">
            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-teal-500 rounded-full opacity-20"></div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-lime-400 rounded-full opacity-20"></div>
            
            <div className="aspect-w-16 aspect-h-9 relative z-10">
              <img 
                src={serviceInfoImg}
                alt="Dental care professional"
                className="w-full h-full object-cover transform transition-transform duration-700 hover:scale-105"
              />
              
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center cursor-pointer hover:shadow-lg transform transition-all duration-300 hover:scale-110">
                  <Play className="w-6 h-6 text-white ml-1" />
                </div>
              </div>
            </div>
          </div>
          
          {/* Service tag */}
          <div className="bg-white px-4 py-2 rounded-lg shadow-md absolute bottom-20 left-4 flex items-center space-x-2 z-20">
            <div className="w-3 h-3 rounded-full bg-lime-400"></div>
            <span className="text-sm font-medium text-gray-700">Professional Service</span>
          </div>

          <Link to="/schedule-appointment" 
            className="cursor-pointer mt-6 inline-flex items-center px-6 py-3 bg-teal-500 text-white font-medium rounded-lg shadow-md hover:bg-teal-600 transition-colors"
            style={{marginLeft: '50%', transform: 'translateX(-50%)'}}>
            Schedule Your Visit
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-2">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServiceInformationSection;